import axios from 'axios';
import {
    PROCESSING_JOB_LIST_REQUEST, PROCESSING_JOB_LIST_SUCCESS, PROCESSING_JOB_LIST_FAIL,
    PROCESSING_JOB_LIST_BY_IDS_REQUEST, PROCESSING_JOB_LIST_BY_IDS_SUCCESS, PROCESSING_JOB_LIST_BY_IDS_FAIL,
    PROCESSING_JOB_DETAILS_REQUEST, PROCESSING_JOB_DETAILS_SUCCESS, PROCESSING_JOB_DETAILS_FAIL,
    PROCESSING_JOB_CREATE_REQUEST, PROCESSING_JOB_CREATE_SUCCESS, PROCESSING_JOB_CREATE_FAIL,
    PROCESSING_JOB_UPDATE_REQUEST, PROCESSING_JOB_UPDATE_SUCCESS, PROCESSING_JOB_UPDATE_FAIL,
    PROCESSING_JOB_DELETE_REQUEST, PROCESSING_JOB_DELETE_SUCCESS, PROCESSING_JOB_DELETE_FAIL,
    PROCESSING_JOB_DTO_REQUEST, PROCESSING_JOB_DTO_SUCCESS, PROCESSING_JOB_DTO_FAIL,
    PROCESSING_JOB_BY_UPLOAD_BATCH_REQUEST, PROCESSING_JOB_BY_UPLOAD_BATCH_SUCCESS, PROCESSING_JOB_BY_UPLOAD_BATCH_FAIL,
    PROCESSING_JOB_BY_STATUS_REQUEST, PROCESSING_JOB_BY_STATUS_SUCCESS, PROCESSING_JOB_BY_STATUS_FAIL,
    PROCESSING_JOB_BY_JOB_TYPE_REQUEST, PROCESSING_JOB_BY_JOB_TYPE_SUCCESS, PROCESSING_JOB_BY_JOB_TYPE_FAIL,
} from '../constants/processingJobConstants';

// Helper functions
const getAuthConfig = (token) => ({
    headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
});

const formatErrorMessage = (error) => {
    const errorMessages = [];
    if (error.response?.data) {
        if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                errorMessages.push(`${key}: ${value[0]}`);
            });
        }
        if (error.response.data.detail) {
            errorMessages.push(`Detail: ${error.response.data.detail}`);
        }
        if (error.response.data.message) {
            errorMessages.push(`Message: ${error.response.data.message}`);
        }
    }
    return errorMessages.length > 0 ? errorMessages : [error.message || "An error occurred, please try again later"];
};

const API_BASE_URL = '/dotnet_api/v1/processingjob';

export const listProcessingJobs = (params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_LIST_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${API_BASE_URL}/all`, config);
        dispatch({ type: PROCESSING_JOB_LIST_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_LIST_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const listProcessingJobsByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_LIST_BY_IDS_REQUEST });
        const { data } = await axios.post(
            `${API_BASE_URL}/ids/all`,
            ids,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PROCESSING_JOB_LIST_BY_IDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_LIST_BY_IDS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getProcessingJobDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_DETAILS_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get(`${API_BASE_URL}/${id}`, config);
        dispatch({
            type: PROCESSING_JOB_DETAILS_SUCCESS,
            payload: data
        });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_DETAILS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const createProcessingJob = (processingJobData) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_CREATE_REQUEST });
        const { data } = await axios.post(
            API_BASE_URL,
            processingJobData,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PROCESSING_JOB_CREATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_CREATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const updateProcessingJob = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_UPDATE_REQUEST });
        const { data } = await axios.patch(
            `${API_BASE_URL}/${id}`,
            changes,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PROCESSING_JOB_UPDATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const deleteProcessingJob = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_DELETE_REQUEST });
        await axios.delete(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PROCESSING_JOB_DELETE_SUCCESS });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_DELETE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const loadProcessingJobDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_DTO_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/dto`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: PROCESSING_JOB_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_DTO_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

// Custom actions for ProcessingJob specific functionality
/** Fetch all pages for CSV export (API caps limit at 500 per page). */
export const fetchAllProcessingJobsByUploadBatch = async (uploadBatchId, params = {}, token) => {
    const limit = 500;
    let page = 1;
    let totalPages = 1;
    const items = [];
    while (page <= totalPages) {
        const { data } = await axios.get(`${API_BASE_URL}/upload-Batch/${uploadBatchId}`, {
            ...getAuthConfig(token),
            params: { ...params, page, limit },
        });
        items.push(...(data.items ?? []));
        totalPages = data.totalPages ?? 1;
        page += 1;
    }
    return items;
};

export const getProcessingJobsByUploadBatch = (uploadBatchId, params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_BY_UPLOAD_BATCH_REQUEST, uploadBatchId });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        console.log('API call config:', config);
        const { data } = await axios.get(`${API_BASE_URL}/upload-Batch/${uploadBatchId}`, config);
        console.log('API response:', data);
        dispatch({ type: PROCESSING_JOB_BY_UPLOAD_BATCH_SUCCESS, payload: data, uploadBatchId });
    } catch (error) {
        console.error('API error:', error);
        dispatch({
            type: PROCESSING_JOB_BY_UPLOAD_BATCH_FAIL,
            payload: formatErrorMessage(error),
            uploadBatchId
        });
    }
};

export const getProcessingJobsByStatus = (status) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_BY_STATUS_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get(`${API_BASE_URL}/status/${status}`, config);
        dispatch({ type: PROCESSING_JOB_BY_STATUS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_BY_STATUS_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const getProcessingJobsByJobType = (jobType) => async (dispatch, getState) => {
    try {
        dispatch({ type: PROCESSING_JOB_BY_JOB_TYPE_REQUEST });
        const config = getAuthConfig(getState().userLogin.userInfo.token);
        const { data } = await axios.get(`${API_BASE_URL}/job-type/${jobType}`, config);
        dispatch({ type: PROCESSING_JOB_BY_JOB_TYPE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: PROCESSING_JOB_BY_JOB_TYPE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
}; 